import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './bonusCountdown.module.scss';

interface BonusCountdownProps {
  unlocksAt: string;
  onComplete?: () => void;
}

export const BonusCountdown: React.FC<BonusCountdownProps> = ({ unlocksAt, onComplete }) => {
  const { t } = useTranslation();
  const [remainingMs, setRemainingMs] = useState(() => {
    const target = new Date(unlocksAt).getTime();
    return Math.max(0, target - Date.now());
  });

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const targetTime = new Date(unlocksAt).getTime();
    let hasCompleted = false;
    
    const updateTime = () => {
      const remaining = targetTime - Date.now();
      if (remaining <= 0) {
        setRemainingMs(0);
        if (!hasCompleted) {
          hasCompleted = true;
          onCompleteRef.current?.();
        }
      } else {
        setRemainingMs(remaining);
      }
      return remaining;
    };

    if (updateTime() <= 0) return;

    const intervalId = setInterval(() => {
      if (updateTime() <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateTime();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', updateTime);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', updateTime);
    };
  }, [unlocksAt]);

  if (remainingMs <= 0) return null;

  const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remainingMs / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((remainingMs / 1000 / 60) % 60);
  const seconds = Math.floor((remainingMs / 1000) % 60);

  const formatNumber = (num: number) => num.toString().padStart(2, '0');

  return (
    <div className={styles.countdownContainer}>
      {days > 0 && (
        <span className={styles.timeBlock}>
          <span className={styles.value}>{formatNumber(days)}</span>
          <span className={styles.unit}>{t('rewards.countdown.d')}</span>
        </span>
      )}
      {(days > 0 || hours > 0) && (
        <span className={styles.timeBlock}>
          <span className={styles.value}>{formatNumber(hours)}</span>
          <span className={styles.unit}>{t('rewards.countdown.h')}</span>
        </span>
      )}
      <span className={styles.timeBlock}>
        <span className={styles.value}>{formatNumber(minutes)}</span>
        <span className={styles.unit}>{t('rewards.countdown.m')}</span>
      </span>
      {days === 0 && hours === 0 && (
        <span className={styles.timeBlock}>
          <span className={styles.value}>{formatNumber(seconds)}</span>
          <span className={styles.unit}>{t('rewards.countdown.s')}</span>
        </span>
      )}
    </div>
  );
};
